import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { toCartTableRows } from './cart-table.mapper';
import { Loading } from '../../../../../utils/loading/loading';
import { EmptyData } from '../../../../../utils/empty-data/empty-data';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';
import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { AdminCart } from '../../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { ActionInterface } from '../../../product-details/product-sub-products/sub-products-list/sub-products-list';


export type LoadState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-cart-list-table',
  standalone: true,
  imports: [Loading, EmptyData, ErrorData, Tables, Pagination, ConfirmationDialog],
  templateUrl: './cart-list-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartListTable {
  private locale = inject(LOCALE_ID);

  carts = input<AdminCart[] | null>(null);
  state = input<LoadState>('loading');
  currentPage = input<number>(1); 
  itemsPerPage = input<number>(25);
  totalItems = input<number>(0);
  totalPages = input<number>(1);
  isFiltersApplied = input<boolean>(false);
  deleteError = input<string>('');
  deleteLoading = input<boolean>(false);
  closeDeleteDialog = input<number>(0);

  pageChange = output<number>();
  pageSizeChange = output<number>();
  retry = output<void>();
  rowClick = output<number>();
  deleteCart = output<number>();
  checkoutCart = output<number>();

  showDeleteDialog = signal(false);
  showCheckoutDialog = signal(false);
  selectedCart = signal<AdminCart | null>(null);
  activeDropdownId = signal<number | null>(null);
  currentActions = signal<ActionInterface[]>([]);

  isArabic = computed(() => this.locale.startsWith('ar'));

  rows = computed(() =>
    toCartTableRows(this.carts() ?? [], this.isArabic(), this.currentPage(), this.itemsPerPage(), this.locale),
  );

  constructor() {
    effect(() => {
      if (this.closeDeleteDialog() > 0) {
        this.showDeleteDialog.set(false);
        this.selectedCart.set(null);
      }
    });
  }

  tableHeaderData = signal([
    { key: 'index', label: $localize`:@@thIndex:#` },
    { key: 'cartTitle', label: $localize`:@@thCartTitle:Cart Title` },
    { key: 'pharmacy', label: $localize`:@@thPharmacy:Pharmacy` },
    { key: 'totalItems', label: $localize`:@@thTotalItems:Items` },
    { key: 'totalQuantity', label: $localize`:@@thTotalQty:Qty` },
    { key: 'totalPrice', label: $localize`:@@thTotalPrice:Total Price` },
    { key: 'createdOnBy', label: $localize`:@@thCreatedOnBy:Created By` },
    { key: 'lastModifiedOnBy', label: $localize`:@@thLastModifiedOnBy:Last Modified` },
    { key: 'assignedTo', label: $localize`:@@thAssignedTo:Assigned` },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  startItem = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1));

  endItem = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems()));

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const delta = 2;
    const range: number[] = [1];
    if (total <= 1) return range;
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) range.push(i);
    }
    range.push(total);
    const result: number[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) result.push(prev + 1);
        else if (i - prev !== 1) result.push(-1);
      }
      result.push(i);
      prev = i;
    }
    return result;
  });

  getActions(): ActionInterface[] {
    return [
      { id: 1, titleEn: 'View Details', titleAr: 'عرض التفاصيل' },
      { id: 2, titleEn: 'Checkout', titleAr: 'إتمام الطلب' },
      { id: 3, titleEn: 'Delete Cart', titleAr: 'حذف العربة' },
      { id: 4, titleEn: 'Duplicate Cart', titleAr: 'نسخ العربة' },
    ];
  }

  toggleDropdown(id?: any) {
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      this.currentActions.set(this.getActions());
    }
  }

  handleAction(e: { event: Event; actionId: number; entity: any }) {
    const { event, actionId, entity } = e;
    event?.stopPropagation();
    this.activeDropdownId.set(null);
    const cart = this.carts()?.find((c) => c.id === entity.id) ?? null;

    switch (actionId) {
      case 1:
        this.rowClick.emit(entity.id);
        break;
      case 2:
        this.selectedCart.set(cart);
        this.showCheckoutDialog.set(true);
        break;
      case 3:
        this.selectedCart.set(cart);
        this.showDeleteDialog.set(true);
        break;
      case 4:
        break;
    }
  }

  onConfirmCheckout() {
    const cart = this.selectedCart();
    if (cart) this.checkoutCart.emit(cart.id);
    this.showCheckoutDialog.set(false);
  }

  onConfirmDelete() {
    const cart = this.selectedCart();
    if (cart) this.deleteCart.emit(cart.id);
  }

  onCancelDelete() {
    this.showDeleteDialog.set(false);
    this.selectedCart.set(null);
  }

  onPreviousPage() {
    this.pageChange.emit(this.currentPage() - 1);
  }
  onNextPage() {
    this.pageChange.emit(this.currentPage() + 1);
  }
  onGoToPage(page: number) {
    this.pageChange.emit(page);
  }
  onChangePageNumber(size: number) {
    this.pageSizeChange.emit(Number(size));
  }
  onPageSizeChange() {}
}
